<template>
  <modal
    v-model="showModal"
    @hide="closeModal"
    :title="modalTitle"
    class="modal--blocker">
    <p class="gb-text-2">{{ modalMessage }}</p>

    <div slot="footer">
      <template v-if="buttonOptions.auth">
        <a :href="signInReturn" class="btn btn-primary">Sign in</a>
        <a class="btn btn-default" :href="signUpReturn">Create an account</a>
      </template>
      <a
        class="btn btn-info"
        role="button"
        :href="toolkitUrls.subscribe"
        v-if="buttonOptions.plans"
      >Request a Quote</a
      >
      <button
        class="btn btn-primary"
        v-if="buttonOptions.ok"
        @click="closeModal">OK</button>
      <button
        class="btn btn-link"
        v-if="buttonOptions.okLink"
        @click="closeModal">OK</button>
      <button
        class="btn btn-default"
        v-if="buttonOptions.cancel"
        @click="closeModal">
        Cancel
      </button>
    </div>
  </modal>
</template>

<script>
import { toolkitUrls, urlHelpers } from '@/links'
import { mapGetters, mapActions } from 'vuex'
import { ACTION_TYPES } from '@/util/access_visibility'

const SignUpForTrialPhrase = 'Sign up for a Goalbook Toolkit trial account'
const MustHaveActiveMembershipPhrase =
  'you must have an active membership through your school or district'
const ContactAdminRenewSentence =
  'Contact your administrator to purchase or renew your premium membership.'
const UnauthorizedSentence = 'You are not authorized to perform this action.'

export default {
  name: 'LimitActionModal',
  props: {
    actionPermissions: {
      type: Object,
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      toolkitUrls,
      showModal: this.show,
    }
  },
  watch: {
    show(newShow) {
      this.showModal = newShow
      if (newShow && this.actionPermissions.blockQuota) {
        this.sendContentAccessBlockedQuota({
          actionType: this.actionPermissions.actionType,
          contentInfo: {
            cu: this.actionPermissions.contentUuid,
            ct: this.actionPermissions.contentTitle,
            ctype: this.actionPermissions.contentType,
          },
        })
      }
    },
  },
  computed: {
    ...mapGetters('user', ['authInfo']),
    signInReturn() {
      return urlHelpers.signInReturn(this.$store.state.currentUrl)
    },
    signUpReturn() {
      return urlHelpers.signUpReturn('toolkit', this.$store.state.currentUrl)
    },
    buttonOptions() {
      // options: ok, cancel, auth, plans, invite
      if (this.actionPermissions.blockUnauthenticated) return { auth: true }
      if (this.actionPermissions.blockTrial || this.actionPermissions.blockExpired)
        return { okLink: true, plans: true }
      if (this.actionPermissions.blockQuota) return { ok: true }
      return {}
    },
    modalTitle() {
      if (this.actionPermissions.blockUnauthenticated)
        return 'You must be signed in to use this feature'
      if (this.actionPermissions.blockExpired) return 'Your membership has expired'
      if (this.actionPermissions.blockTrial) return 'Feature available with premium membership'

      if (
        this.actionPermissions.actionType === ACTION_TYPES.DOWNLOAD &&
        this.actionPermissions.blockQuota
      ) {
        return 'Monthly download limit reached'
      }
      if (
        this.actionPermissions.actionType === ACTION_TYPES.COPY &&
        this.actionPermissions.blockQuota
      ) {
        return 'Monthly copy limit reached'
      }
      if (
        this.actionPermissions.actionType === ACTION_TYPES.PRINT &&
        this.actionPermissions.blockQuota
      ) {
        return 'Monthly print limit reached'
      }
      return 'Unauthorized'
    },
    modalMessage() {
      if (this.actionPermissions.actionType === ACTION_TYPES.SAVE) {
        return (
          (this.actionPermissions.blockUnauthenticated &&
            `${SignUpForTrialPhrase} to save this page.`) ||
          (this.actionPermissions.blockExpired &&
            'To save this page, you must have an active membership.') ||
          UnauthorizedSentence
        )
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.TRANSLATE) {
        return (
          (this.actionPermissions.blockUnauthenticated &&
            `${SignUpForTrialPhrase} to translate this content.`) ||
          (this.actionPermissions.blockExpired &&
            'To translate this content, you must have an active membership.') ||
          UnauthorizedSentence
        )
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.COPY_STUDENT) {
        return `To copy a folder, ${MustHaveActiveMembershipPhrase}. ${ContactAdminRenewSentence}`
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.DOWNLOAD) {
        return (
          (this.actionPermissions.blockUnauthenticated &&
            `${SignUpForTrialPhrase} to download this resource.`) ||
          (this.actionPermissions.blockTrial &&
            `To download this resource, ${MustHaveActiveMembershipPhrase}.`) ||
          (this.actionPermissions.blockQuota &&
            'You have already used all of your available downloads for this month. If you believe this is an error, contact us at support@goalbookapp.com.') ||
          (this.actionPermissions.blockExpired &&
            `To download this resource, ${MustHaveActiveMembershipPhrase}. ${ContactAdminRenewSentence}`) ||
          UnauthorizedSentence
        )
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.COPY) {
        return (
          (this.actionPermissions.blockQuota &&
            'You have already used all of your available copies for this month. We have a quota in place to make sure our content is not being abused. If you believe this to be an error, contact us at support@goalbookapp.com.') ||
          (this.actionPermissions.blockExpired &&
            `To copy this text ${MustHaveActiveMembershipPhrase}. ${ContactAdminRenewSentence}`) ||
          UnauthorizedSentence
        )
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.PRINT) {
        return (
          (this.actionPermissions.blockUnauthenticated &&
            `${SignUpForTrialPhrase} to print this content.`) ||
          (this.actionPermissions.blockTrial &&
            `To print this content, ${MustHaveActiveMembershipPhrase}.`) ||
          (this.actionPermissions.blockQuota &&
            'You have already used all of your available prints for this month. If you believe this is an error, contact us at support@goalbookapp.com.') ||
          (this.actionPermissions.blockExpired &&
            `To print this content, ${MustHaveActiveMembershipPhrase}. ${ContactAdminRenewSentence}`) ||
          UnauthorizedSentence
        )
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.SHARE) {
        return (
          (this.actionPermissions.blockTrial &&
            `To share this content, ${MustHaveActiveMembershipPhrase}.`) ||
          (this.actionPermissions.blockExpired &&
            `To share this content, ${MustHaveActiveMembershipPhrase}. ${ContactAdminRenewSentence}`) ||
          UnauthorizedSentence
        )
      }
      if (this.actionPermissions.actionType === ACTION_TYPES.DEVELOP_PLW) {
        return (
          (this.actionPermissions.blockUnauthenticated &&
            `${SignUpForTrialPhrase} to use the Present Levels Wizard.`) ||
          (this.actionPermissions.blockExpired &&
            `To use the Present Levels Wizard, ${MustHaveActiveMembershipPhrase}. ${ContactAdminRenewSentence}`) ||
          UnauthorizedSentence
        )
      }
    },
  },
  methods: {
    closeModal() {
      this.$emit('update:show', false)
    },
    ...mapActions('contentAccess', ['sendContentAccessBlockedQuota']),
  },
}
</script>

<style lang="less">
.modal--blocker {
  color: var(--gray-default);

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 16px 20px;
  }
  .modal-header,
  .modal-footer {
    border: 0;
  }
  .modal-title {
    font-size: 24px;
    font-weight: normal;
  }
  .btn-info {
    background-color: var(--blue-default);
    border: none;

    &:hover,
    &:focus {
      background-color: var(--blue-button-hover);
    }

    &:focus {
      outline: solid 2px var(--blue-button-hover);
      outline-offset: 2px;
    }
  }
}
</style>
